import { computed, onScopeDispose, ref, shallowRef, toValue, watch } from 'vue'
import type { MaybeRefOrGetter, Ref, ShallowRef } from 'vue'

import type { CreativePreview } from '../models/credit.types'
import { useCreditStore } from '../stores/creditStore'

import { useWorkspace } from '@/features/account'

export const CREATIVE_PREVIEW_DEBOUNCE_MS = 350

export type CreativeType = 'image' | 'video'

export interface CreativeCostPreviewInput {
  creativeType: CreativeType
  dateFrom?: string
  dateTo?: string
  filters?: unknown
}

export interface CreativePreviewStoreParams {
  creative_type: CreativeType
  date_from: string
  date_to: string
  workspace_id: number
  filters?: unknown
}

export interface CreativePreviewChange {
  affordable: boolean
  affordableCount: number
  chargeable: number
  /** Creatives in scope; `0` means nothing matches the filters (legacy `hasEmptyScope`). */
  total: number
  analysisAffordable: boolean
}

export type CreativePreviewState = 'idle' | 'loading' | 'ready' | 'error'

export interface UseCreativeCostPreviewResult {
  preview: ShallowRef<CreativePreview | null>
  state: Ref<CreativePreviewState>
  error: ShallowRef<unknown>
  hasConfig: Readonly<Ref<boolean>>
  loading: Readonly<Ref<boolean>>
  config: Readonly<Ref<CreativePreview['config'] | null>>
  chargeable: Readonly<Ref<number>>
  affordableCount: Readonly<Ref<number>>
  analysisAffordable: Readonly<Ref<boolean>>
  cost: Readonly<Ref<number>>
  affordable: Readonly<Ref<boolean>>
  refresh: () => Promise<void>
}

export function useCreativeCostPreview(
  input: MaybeRefOrGetter<CreativeCostPreviewInput>,
  onChange?: (change: CreativePreviewChange) => void,
): UseCreativeCostPreviewResult {
  const creditStore = useCreditStore()
  const { workspaceId } = useWorkspace()
  const preview = shallowRef<CreativePreview | null>(null)
  const error = shallowRef<unknown>(null)
  const state = ref<CreativePreviewState>('idle')
  let timer: ReturnType<typeof setTimeout> | undefined
  let requestGeneration = 0

  const numericWorkspaceId = computed(() => {
    const parsed = Number(workspaceId.value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  })
  const hasConfig = computed(() => {
    const value = toValue(input)
    return Boolean(value.dateFrom && value.dateTo && numericWorkspaceId.value !== null)
  })
  const loading = computed(() => state.value === 'loading')
  const config = computed(() => preview.value?.config ?? null)
  const chargeable = computed(() => config.value?.chargeable ?? 0)
  const affordableCount = computed(() => {
    if (!preview.value || !config.value) return 0
    return preview.value.affordable
      ? config.value.chargeable
      : preview.value.affordable_subset.images + preview.value.affordable_subset.videos
  })
  const analysisAffordable = computed(
    () => !preview.value?.analysis || preview.value.analysis.affordable,
  )
  const cost = computed(() => preview.value?.cost ?? 0)
  const affordable = computed(() => preview.value?.affordable ?? false)

  function cancelScheduledRequest(): void {
    if (timer === undefined) return
    clearTimeout(timer)
    timer = undefined
  }

  function buildParams(): CreativePreviewStoreParams | null {
    const value = toValue(input)
    const activeWorkspaceId = numericWorkspaceId.value
    if (!value.dateFrom || !value.dateTo || activeWorkspaceId === null) return null

    return {
      creative_type: value.creativeType,
      date_from: value.dateFrom,
      date_to: value.dateTo,
      workspace_id: activeWorkspaceId,
      ...(value.filters === undefined ? {} : { filters: value.filters }),
    }
  }

  async function load(generation: number): Promise<void> {
    const params = buildParams()
    if (!params) return

    state.value = 'loading'
    error.value = null

    try {
      const nextPreview = await creditStore.previewCreativeConfig(params)
      if (generation !== requestGeneration) return
      if (!nextPreview) {
        preview.value = null
        state.value = 'idle'
        return
      }

      preview.value = nextPreview
      state.value = 'ready'
      onChange?.({
        affordable: nextPreview.affordable,
        affordableCount: nextPreview.affordable
          ? (nextPreview.config?.chargeable ?? 0)
          : nextPreview.affordable_subset.images + nextPreview.affordable_subset.videos,
        chargeable: nextPreview.config?.chargeable ?? 0,
        total: nextPreview.config?.total ?? 0,
        analysisAffordable: !nextPreview.analysis || nextPreview.analysis.affordable,
      })
    } catch (cause: unknown) {
      if (generation !== requestGeneration) return
      preview.value = null
      error.value = cause
      state.value = 'error'
    }
  }

  function schedule(): void {
    cancelScheduledRequest()
    const generation = ++requestGeneration

    if (!hasConfig.value) {
      preview.value = null
      error.value = null
      state.value = 'idle'
      return
    }

    // Legacy parity: the debounce window already reads as loading, so a submit gated on it never
    // acts on the estimate the input change just made stale.
    state.value = 'loading'
    timer = setTimeout(() => {
      timer = undefined
      void load(generation)
    }, CREATIVE_PREVIEW_DEBOUNCE_MS)
  }

  async function refresh(): Promise<void> {
    cancelScheduledRequest()
    const generation = ++requestGeneration
    if (!hasConfig.value) {
      preview.value = null
      error.value = null
      state.value = 'idle'
      return
    }
    await load(generation)
  }

  watch(
    () => {
      const value = toValue(input)
      return [
        value.creativeType,
        value.dateFrom,
        value.dateTo,
        value.filters,
        workspaceId.value,
      ] as const
    },
    schedule,
    { deep: true, immediate: true },
  )

  onScopeDispose(() => {
    cancelScheduledRequest()
    requestGeneration += 1
  })

  return {
    preview,
    state,
    error,
    hasConfig,
    loading,
    config,
    chargeable,
    affordableCount,
    analysisAffordable,
    cost,
    affordable,
    refresh,
  }
}
